import { Column } from "../objects/column";
import { CustomInsurance } from "../objects/customInsurance";
import { CustomInsurancePrice, PriceDimension, RangeCriterion, TierField } from "../objects/customInsuranceContract";
import { ContractPriceGridColumn } from "../utils/contractPriceGrid";
import { CustomInsuranceListConfiguration, CustomInsuranceListCriteria, CustomInsuranceListFiltersCriteria } from "../objects/customInsuranceTypeList";

export const CUSTOM_INSURANCE_MODULE = 'CUSTOM_INSURANCE';

export const EMPTY_CUSTOM_INSURANCE: CustomInsurance = {
    name: '',
    short_description: null,
    long_description: null,
    localization: [],
    provider: null,
    policy_reference: null,
    contracts: [],
    selection_type: 'OPTIONAL',
    is_preselected: false,
    upgrade_of: [],
    price_visibility: 'SEPARATE_LINE',
    visibility: 'PUBLIC',
    pricing_type: 'PURCHASE',
    global_com: null,
    com_is_percentage: true,
    global_markup: null,
    markup_is_percentage: true,
    agency_sees_price: true,
    agency_sees_margin: false,
    agency_sees_policy_reference: true,
    agency_sees_ceilings: true,
    required_product_categories: [],
    allowed_cms: [],
    allowed_on_apps: true,
    subscription_window_hours: 24,
    max_days_before_departure: null,
    baggage_scope: null,
    coverage_levels: [],
    prices: [],
    rule_groups: [],
    documents: [],
    picture: null,
    created_date: new Date(),
    modified_date: new Date(),
    creator: null,
    last_author: null,
};

export const EMPTY_CUSTOM_INSURANCE_FILTERS: CustomInsuranceListFiltersCriteria = {
    creator: "",
    last_author: "",
    created_date_gte: null,
    created_date_lte: null,
    modified_date_gte: null,
    modified_date_lte: null,
    period_type: 0,
    period: "MODIFICATION",
    selection_type: "",
    visibility: "",
    /** Par défaut la liste ne montre que les produits actifs (non supprimés). */
    is_active: "true",
    upgrade_of: [],
};

export const EMPTY_CUSTOM_INSURANCE_LIST_CRITERIA: CustomInsuranceListCriteria = {
    limit: 50,
    page: 0,
    order_by: '',
    order: 'asc',
    filters: EMPTY_CUSTOM_INSURANCE_FILTERS,
    search: '',
};

/** `order_by` vide partout : l'API n'expose pas encore d'OrderingFilter sur ce endpoint. */
export const DEFAULT_COLUMN: Column[] = [
    { label: 'shared.name', field_name: 'NAME', checked: true, order_by: '', fixed_width: false, last_updated: false },
    { label: 'insurance.catalog-provider', field_name: 'PROVIDER', checked: true, order_by: '', fixed_width: false, last_updated: false },
    { label: 'insurance.catalog-selection-type', field_name: 'SELECTION_TYPE', checked: true, order_by: '', fixed_width: true, last_updated: false },
    { label: 'insurance.catalog-price-visibility', field_name: 'PRICE_VISIBILITY', checked: true, order_by: '', fixed_width: true, last_updated: false },
    { label: 'insurance.catalog-active-contract', field_name: 'ACTIVE_CONTRACT', checked: true, order_by: '', fixed_width: false, last_updated: false },
    { label: 'shared.online-to', field_name: 'VISIBILITY', checked: true, order_by: '', fixed_width: true, last_updated: false },
    { label: 'shared.online-agency', field_name: 'AGENCY_VISIBILITY', checked: true, order_by: '', fixed_width: true, last_updated: false },
    { label: 'insurance.catalog-status', field_name: 'IS_ACTIVE', checked: true, order_by: '', fixed_width: true, last_updated: false },
    { label: 'insurance.catalog-policy-reference', field_name: 'POLICY_REFERENCE', checked: false, order_by: '', fixed_width: true, last_updated: false },
    { label: 'shared.created-date', field_name: 'CREATED_DATE', checked: false, order_by: '', fixed_width: true, last_updated: false },
    { label: 'shared.created-by', field_name: 'CREATED_BY', checked: false, order_by: '', fixed_width: true, last_updated: false },
    { label: 'shared.modified-date', field_name: 'MODIFIED_DATE', checked: true, order_by: '', fixed_width: true, last_updated: false },
    { label: 'shared.modified-by', field_name: 'MODIFIED_BY', checked: false, order_by: '', fixed_width: true, last_updated: false },
];

export const EMPTY_CUSTOM_INSURANCE_LIST_CONFIGURATION: CustomInsuranceListConfiguration = {
    columns: DEFAULT_COLUMN,
    preferences: [],
};

export const SELECTION_TYPE_OPTIONS: { value: string, label: string }[] = [
    { value: 'MANDATORY', label: 'insurance.selection-type-mandatory' },
    { value: 'OPTIONAL', label: 'insurance.selection-type-optional' },
    { value: 'UPGRADE', label: 'insurance.selection-type-upgrade' },
];

export const PRICE_VISIBILITY_OPTIONS: { value: string, label: string }[] = [
    { value: 'SEPARATE_LINE', label: 'insurance.price-visibility-separate-line' },
    { value: 'MERGED_IN_FEES', label: 'insurance.price-visibility-merged-in-fees' },
    { value: 'HIDDEN', label: 'insurance.price-visibility-hidden' },
];

/** Filtre « Visibilité » de la liste (`?visibility=`), « Tous » = vide. */
export const VISIBILITY_FILTER_OPTIONS: { value: string, label: string }[] = [
    { value: '', label: 'shared.all' },
    { value: 'PUBLIC', label: 'shared.online-b2b' },
    { value: 'PRIVATE_TO', label: 'shared.online-to' },
    { value: 'WAITING', label: 'insurance.visibility-waiting' },
    { value: 'PRIVATE', label: 'shared.offline' },
];

/** Filtre « Statut » de la liste (`?is_active=`) — ses libellés servent aussi à la colonne « Statut ». */
export const IS_ACTIVE_FILTER_OPTIONS: { value: string, label: string }[] = [
    { value: '', label: 'shared.all' },
    { value: 'true', label: 'insurance.status-active' },
    { value: 'false', label: 'insurance.status-deleted' },
];

export const VISIBILITY_OPTIONS: { value: string, label: string }[] = [
    { value: 'PUBLIC', label: 'insurance.visibility-public' },
    { value: 'WAITING', label: 'insurance.visibility-waiting' },
    { value: 'PRIVATE_TO', label: 'insurance.visibility-private-to' },
    { value: 'PRIVATE', label: 'insurance.visibility-private' },
];

/** Mêmes valeurs et clés i18n que customProvider/customProviderContentType.tsx. */
export const PRODUCT_TYPE_OPTIONS: { value: number, label: string }[] = [
    { value: 0, label: 'providers.hotels' },
    { value: 1, label: 'providers.excursions' },
    { value: 2, label: 'providers.rental_car' },
    { value: 3, label: 'providers.restaurant' },
    { value: 4, label: 'providers.transfers' },
    { value: 5, label: 'providers.trains' },
    { value: 6, label: 'shared.flights' },
    { value: 7, label: 'providers.accommodations' },
    { value: 8, label: 'providers.insurances' },
    { value: 9, label: 'providers.cruises' },
    { value: 10, label: 'providers.touristic_guide' },
    { value: 11, label: 'providers.other_product' },
    { value: 12, label: 'providers.activities' },
    { value: 13, label: 'providers.ferries' },
    { value: 16, label: 'providers.administration_fees' },
    { value: 17, label: 'providers.travel_book' },
    { value: 24, label: 'content-types.24' },
];

export const PRICING_TYPE_OPTIONS: { value: string, label: string }[] = [
    { value: 'PURCHASE', label: 'insurance.pricing-type-purchase' },
    { value: 'PURCHASE_COM', label: 'insurance.pricing-type-com' },
    { value: 'PURCHASE_MARKUP', label: 'insurance.pricing-type-markup' },
    { value: 'PURCHASE_DETAILED', label: 'insurance.pricing-type-detailed' },
];

/** Drapeaux de visibilité agence, dans l'ordre d'affichage. */
export const AGENCY_VISIBILITY_FLAGS: { field: 'agency_sees_price' | 'agency_sees_margin' | 'agency_sees_policy_reference' | 'agency_sees_ceilings', label: string, hint: string }[] = [
    { field: 'agency_sees_price', label: 'insurance.agency-sees-price', hint: 'insurance.agency-sees-price-hint' },
    { field: 'agency_sees_margin', label: 'insurance.agency-sees-margin', hint: 'insurance.agency-sees-margin-hint' },
    { field: 'agency_sees_policy_reference', label: 'insurance.agency-sees-policy-reference', hint: 'insurance.agency-sees-policy-reference-hint' },
    { field: 'agency_sees_ceilings', label: 'insurance.agency-sees-ceilings', hint: 'insurance.agency-sees-ceilings-hint' },
];

export const BAGGAGE_SCOPE_OPTIONS: { value: string, label: string }[] = [
    { value: 'ALL', label: 'insurance.baggage-scope-all' },
    { value: 'CHECKED_ONLY', label: 'insurance.baggage-scope-checked-only' },
    { value: 'EXTRA_BAGGAGE', label: 'insurance.baggage-scope-extra-baggage' },
];

export const PRICE_UNIT_OPTIONS: { value: string, label: string }[] = [
    { value: 'PER_TRAVELER', label: 'insurance.price-unit-per-traveler' },
    { value: 'PER_FILE', label: 'insurance.price-unit-per-file' },
    { value: 'PER_BAGGAGE', label: 'insurance.price-unit-per-baggage' },
    { value: 'PER_UNIT', label: 'insurance.price-unit-per-unit' },
];

export const CEILING_SCOPE_OPTIONS: { value: string, label: string }[] = [
    { value: 'PER_PERSON', label: 'insurance.ceiling-scope-per-person' },
    { value: 'PER_FILE', label: 'insurance.ceiling-scope-per-file' },
    { value: 'MEDICAL', label: 'insurance.ceiling-scope-medical' },
    { value: 'BAGGAGE', label: 'insurance.ceiling-scope-baggage' },
    { value: 'CANCELLATION', label: 'insurance.ceiling-scope-cancellation' },
    { value: 'OTHER', label: 'insurance.ceiling-scope-other' },
];

export const TIER_FIELD_OPTIONS: { value: TierField, label: string }[] = [
    { value: 'NIGHTS', label: 'insurance.tier-field-nights' },
    { value: 'FILE_AMOUNT', label: 'insurance.tier-field-file-amount' },
    { value: 'PAX_COUNT', label: 'insurance.tier-field-pax-count' },
];

/** Ordre des colonnes de la grille de prix d'un contrat. */
export const PRICE_DIMENSIONS: PriceDimension[] = ['ZONE', 'NIGHTS', 'FILE_AMOUNT', 'PAX_COUNT', 'AGE', 'LEVEL'];
/** Critères à bornes, dans l'ordre des colonnes, avec leur paire de champs — seule source pour le type, la grille
 * avancée, le tableau de prix, son codage et ses contrôles. */
export const RANGE_CRITERIA: { criterion: RangeCriterion, min: RangeBoundField, max: RangeBoundField }[] = [
    { criterion: 'NIGHTS', min: 'min_nights', max: 'max_nights' },
    { criterion: 'FILE_AMOUNT', min: 'min_file_amount', max: 'max_file_amount' },
    { criterion: 'PAX_COUNT', min: 'min_pax', max: 'max_pax' },
    { criterion: 'AGE', min: 'min_age', max: 'max_age' },
];
export type RangeBoundField = 'min_nights' | 'max_nights' | 'min_file_amount' | 'max_file_amount' | 'min_pax' | 'max_pax' | 'min_age' | 'max_age';
export const RANGE_BOUND_FIELDS: RangeBoundField[] = RANGE_CRITERIA.flatMap(({ min, max }) => [min, max]);

export const PRICE_DIMENSION_OPTIONS: { value: PriceDimension, label: string }[] = [
    { value: 'ZONE', label: 'insurance.dimension-zone' },
    { value: 'NIGHTS', label: 'insurance.tier-field-nights' },
    { value: 'FILE_AMOUNT', label: 'insurance.tier-field-file-amount' },
    { value: 'PAX_COUNT', label: 'insurance.tier-field-pax-count' },
    { value: 'AGE', label: 'insurance.dimension-age' },
    { value: 'LEVEL', label: 'insurance.dimension-level' },
];

export const GRID_COLUMN_LABELS: Record<ContractPriceGridColumn, string> = {
    SCOPE: 'insurance.grid-scope',
    ZONE: 'insurance.dimension-zone',
    NIGHTS: 'insurance.tier-field-nights',
    FILE_AMOUNT: 'insurance.tier-field-file-amount',
    PAX_COUNT: 'insurance.tier-field-pax-count',
    AGE: 'insurance.dimension-age',
    LEVEL: 'insurance.dimension-level',
    LEVEL_MODE: 'insurance.grid-level-mode',
    AMOUNT: 'insurance.grid-amount',
    UNIT: 'insurance.grid-unit',
    PERCENT: 'insurance.grid-percent',
};

export const PRICE_SCOPE_OPTIONS: { value: string, label: string }[] = [
    { value: 'ALL', label: 'insurance.grid-scope-all' },
    { value: 'PRODUCT', label: 'insurance.grid-scope-product' },
];

/** Motifs d'échec de tarification (aperçu, alertes de tarification). */
export const PRICING_FAILURE_REASON_LABELS: Record<string, string> = {
    NO_ACTIVE_CONTRACT: 'insurance.pricing-reason-no-active-contract',
    NO_PRICE_ROW: 'insurance.pricing-reason-no-price-row',
    AGE_OUT_OF_BAND: 'insurance.pricing-reason-age-out-of-band',
};

/** Type de prix du tableau : quelles parts (montant, % du montant voyage) chaque cellule porte. */
export const PRICE_TYPE_OPTIONS: { value: string, label: string }[] = [
    { value: 'FIXED', label: 'insurance.price-type-fixed' },
    { value: 'VARIABLE', label: 'insurance.price-type-percent' },
    { value: 'MIXED', label: 'insurance.price-type-mixed' },
];

export const LEVEL_PRICE_MODE_OPTIONS: { value: string, label: string }[] = [
    { value: 'ABSOLUTE', label: 'insurance.level-price-mode-absolute' },
    { value: 'DELTA_FROM_BASE', label: 'insurance.level-price-mode-delta' },
];

export const CONDITION_OPERATOR_OPTIONS: { value: string, label: string }[] = [
    { value: 'EQ', label: 'insurance.operator-eq' },
    { value: 'NEQ', label: 'insurance.operator-neq' },
    { value: 'IN', label: 'insurance.operator-in' },
    { value: 'NOT_IN', label: 'insurance.operator-not-in' },
    { value: 'GTE', label: 'insurance.operator-gte' },
    { value: 'LTE', label: 'insurance.operator-lte' },
    { value: 'BETWEEN', label: 'insurance.operator-between' },
    { value: 'IS_TRUE', label: 'insurance.operator-is-true' },
    { value: 'IS_FALSE', label: 'insurance.operator-is-false' },
];

/** Niveau de couverture de base par défaut (tout produit en a au moins un). */
export const EMPTY_COVERAGE_LEVEL = {
    name: '',
    is_base: true,
    order: 0,
    ceilings: [],
    localization: [],
};

/** Ligne de prix vierge : tous produits liés, toutes zones, sans tranche, prix fixe par voyageur à saisir. */
export const EMPTY_CONTRACT_PRICE: CustomInsurancePrice = {
    product: null,
    coverage_level: null,
    destinations: [],
    destination_zone: null,
    min_nights: null,
    max_nights: null,
    min_file_amount: null,
    max_file_amount: null,
    min_pax: null,
    max_pax: null,
    min_age: null,
    max_age: null,
    purchase_amount: null,
    price_unit: 'PER_TRAVELER',
    purchase_percent: null,
    level_price_mode: 'ABSOLUTE',
};
